import React, { useContext } from 'react';
import { useTheme } from '@emotion/react';
import { STable } from '@chargebee/sting-react';
import { type InlineEditorHints } from '@bbk/ui/inline-editing';
import {
  type RestFeatureComparisonCell,
  type RestFeatureComparisonLaCard,
} from '@bbkAdminUtils/api-client/rest-la-cards-types';
import { type RestModalForwards } from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { cs } from '@bbkAdminUtils/css-utils';
import {
  FEATURE_COMPARISON_DEFAULT_NEGATIVE_ICON,
  FEATURE_COMPARISON_DEFAULT_POSITIVE_ICON,
  getFeatureComparisonCell,
  getFeatureComparisonColumnCount,
} from '@bbkAdminUtils/feature-comparison';
import CustomerHtml from '@bbkFunnel/components/customer_html/customer_html';
import { OtherActions } from '@bbkFunnel/components/modals/sections/other-actions';
import { FunnelButton } from '@bbkFunnel/components/v2';
import { ThemeDefaults, type EmotionTheme } from '@bbkFunnel/pages/theme';
import { FunnelContext } from '@bbkFunnelComponents/FunnelContext';
import getIconForItem from '@bbkFunnelComponents/loss/loss-icons';
import { LossGridItem } from '@bbkFunnelComponents/loss/sections/grid-item';
import { LossTitle } from '@bbkFunnelComponents/loss/sections/title';
import ActionButtonIcons from '@bbkFunnelComponents/modals/sections/action-button-helpers';
import Icon from '@bbkFunnelUtils/exit-icon';
import { makeClassNameSafe } from '@bbkFunnelUtils/utils';

type Props = {
  card: RestFeatureComparisonLaCard;
  onClick?: () => void;
  handleLossCardAction?: ({ action }: { action: RestModalForwards }) => void;
  isHorizontal: boolean;
};

// Cell styles are set from the table wrapper. The admin preview also loads
// Sting CSS, and these selectors need to beat Sting's own cell classes there.
const tableClasses = cs(
  '!tw-overflow-visible !tw-rounded-none',
  '[&>table]:tw-w-full [&>table]:tw-border-collapse',
  '[&_td]:tw-align-middle [&_th]:tw-align-middle',
  '[&_td]:tw-text-[0.88em] [&_th]:tw-text-[0.88em]',
  '[&_td]:tw-leading-snug [&_th]:tw-leading-snug',
  '[&_thead_th]:tw-px-2 [&_thead_th]:tw-py-2 [&_thead_th]:tw-text-center',
  '[&_thead_th]:tw-font-bold [&_thead_th]:tw-uppercase [&_thead_th]:tw-tracking-wide',
  '[&_thead_th:first-of-type]:tw-text-left',
  '[&_tbody_th]:tw-px-2 [&_tbody_th]:tw-py-2.5 [&_tbody_th]:tw-text-left [&_tbody_th]:tw-font-normal',
  '[&_td]:tw-px-2 [&_td]:tw-py-2.5 [&_td]:tw-text-center'
);

const iconBoxClasses =
  'tw-inline-flex tw-size-[22px] tw-items-center tw-justify-center';

const RenderLossFeatureComparison: React.FC<Props> = ({
  card,
  card: {
    id,
    max_redemptions_reached,
    requires,
    requires: {
      title,
      description,
      feature_column_header,
      comparison_columns = [],
      positive_icon,
      negative_icon,
      positive_icon_picker,
      negative_icon_picker,
      rows = [],
      forwards,
      other_actions,
    },
  },
  onClick,
  handleLossCardAction,
  isHorizontal,
}) => {
  const theme = useTheme() as EmotionTheme;
  const { isPreview } = useContext(FunnelContext);

  const colCount = getFeatureComparisonColumnCount(requires);
  const statusLabels = theme.featureComparisonStatusLabels ?? {
    positive: ThemeDefaults.FEATURE_COMPARISON_INCLUDED,
    negative: ThemeDefaults.FEATURE_COMPARISON_NOT_INCLUDED,
  };

  const specificStyles = {
    backgroundColor: theme.buttonBackgroundColor,
    width: '100%',
  };

  const iconFill =
    (card as { style_overlay?: { fillIconColor?: string } }).style_overlay
      ?.fillIconColor ?? theme.primaryColor;

  const renderPackIcon = (iconId: string | undefined) => {
    if (!iconId || iconId === 'none') return null;
    return (
      <span
        className={cs(
          iconBoxClasses,
          '[&_path]:tw-fill-[inherit] [&_polygon]:tw-fill-[inherit] [&_svg]:tw-size-[18px] [&_svg]:tw-fill-[inherit]'
        )}
      >
        {getIconForItem(iconId, iconFill)}
      </span>
    );
  };

  // Preflight resets can make <img> a block, which skips the cell's text-align.
  const renderUploadedIcon = (src: string) => (
    <span className={iconBoxClasses}>
      <img src={src} alt="" className="tw-size-5 tw-object-contain" />
    </span>
  );

  const renderStatusIcon = (cell: RestFeatureComparisonCell) => {
    const isPositive = cell.status === 'positive';
    const defaultUpload = isPositive ? positive_icon : negative_icon;
    const defaultPicker =
      (isPositive ? positive_icon_picker : negative_icon_picker) ||
      (isPositive
        ? FEATURE_COMPARISON_DEFAULT_POSITIVE_ICON
        : FEATURE_COMPARISON_DEFAULT_NEGATIVE_ICON);

    if (cell.icon) return renderUploadedIcon(cell.icon);
    const overridePack = renderPackIcon(cell.icon_picker);
    if (overridePack) return overridePack;
    if (defaultUpload) return renderUploadedIcon(defaultUpload);
    const defaultPack = renderPackIcon(defaultPicker);
    if (defaultPack) return defaultPack;

    return (
      <span
        className={cs(
          iconBoxClasses,
          'tw-rounded-full',
          '[&_path]:tw-fill-current [&_svg]:tw-size-3 [&_svg]:tw-fill-current',
          isPositive
            ? 'tw-bg-warn-400 tw-text-neutral-900'
            : 'tw-bg-neutral-400/45 tw-text-neutral-0'
        )}
      >
        <Icon
          name={isPositive ? 'checkmark' : 'close'}
          classes="tw-flex tw-items-center"
        />
      </span>
    );
  };

  return (
    <LossGridItem
      classes={cs(
        'bbk-funnel-loss-feature-comparison',
        makeClassNameSafe(card.name)
      )}
      onClick={isPreview ? onClick : () => undefined}
      isHorizontal={isHorizontal}
    >
      <div
        className={cs('tw-box-border tw-flex tw-h-full tw-w-full tw-flex-col', {
          'tw-p-[30px]': isHorizontal,
        })}
      >
        <LossTitle>
          <span data-part="title">
            <CustomerHtml value={title} />
          </span>
        </LossTitle>
        {description && (
          <div
            className={cs(
              'tw-mb-4 tw-text-[0.9em] tw-leading-snug tw-opacity-85',
              { 'tw-text-neutral-500': !theme.textColor }
            )}
            style={theme.textColor ? { color: theme.textColor } : undefined}
          >
            <CustomerHtml value={description} />
          </div>
        )}

        <div
          data-part="table"
          className="tw-w-full tw-overflow-x-auto [&_.sting-preflight]:tw-font-[inherit]"
        >
          <STable className={tableClasses} border="horizontal">
            <STable.Head background="transparent">
              <STable.Row>
                <STable.HeaderCell scope="col" truncate={false}>
                  <CustomerHtml value={feature_column_header} />
                </STable.HeaderCell>
                {Array.from({ length: colCount }, (_, colIdx) => (
                  <STable.HeaderCell
                    key={`header-${colIdx}`}
                    scope="col"
                    truncate={false}
                  >
                    <CustomerHtml
                      value={
                        comparison_columns[colIdx]?.header ??
                        `Column ${colIdx + 1}`
                      }
                    />
                  </STable.HeaderCell>
                ))}
              </STable.Row>
            </STable.Head>
            <STable.Body>
              {rows.map((row, rowIdx) => (
                <STable.Row
                  key={`row-${rowIdx}`}
                  className={cs(
                    'tw-border-0 tw-border-t tw-border-solid tw-border-black/5',
                    rowIdx % 2 === 1
                      ? 'tw-bg-black/5 hover:!tw-bg-black/5'
                      : 'hover:!tw-bg-transparent'
                  )}
                >
                  <STable.HeaderCell
                    scope="row"
                    truncate={false}
                    className="bbk-fc-feature-cell"
                  >
                    <CustomerHtml value={row.label} />
                  </STable.HeaderCell>
                  {Array.from({ length: colCount }, (_, colIdx) => {
                    const cell = getFeatureComparisonCell(row.cells, colIdx);
                    const isPositive = cell.status === 'positive';
                    return (
                      <STable.Cell
                        key={`cell-${rowIdx}-${colIdx}`}
                        truncate={false}
                        className={cs('bbk-fc-status-cell', {
                          'bbk-fc-positive': isPositive,
                          'bbk-fc-negative': !isPositive,
                        })}
                      >
                        <span className="tw-sr-only">
                          {isPositive
                            ? statusLabels.positive
                            : statusLabels.negative}
                        </span>
                        <span aria-hidden="true">{renderStatusIcon(cell)}</span>
                      </STable.Cell>
                    );
                  })}
                </STable.Row>
              ))}
            </STable.Body>
          </STable>
        </div>

        <div>
          {forwards && (
            <div
              className={cs(
                'btn',
                'tw-mt-4 tw-flex tw-items-center tw-justify-center'
              )}
              data-part="action"
            >
              <FunnelButton
                disabled={max_redemptions_reached}
                onClick={() => {
                  handleLossCardAction?.({ action: forwards });
                }}
                specificStyles={specificStyles}
              >
                {forwards?.icon && (
                  <ActionButtonIcons
                    styles={{ marginRight: 4 }}
                    icon={forwards.icon}
                  />
                )}
                {forwards.text}
              </FunnelButton>
            </div>
          )}
          {other_actions && (
            <OtherActions
              version2Template
              id={id}
              otherActions={other_actions}
              onClick={(action) => handleLossCardAction?.({ action })}
            />
          )}
        </div>
      </div>
    </LossGridItem>
  );
};

const LossFeatureComparison = Object.assign(RenderLossFeatureComparison, {
  editorHints: {
    title: {
      path: ['requires'],
      control: 'group',
      selector: '[data-part="title"]',
      placement: 'top-end',
      fields: [
        {
          path: ['title'],
          control: 'richText',
          label: 'Headline',
        },
        {
          path: ['description'],
          control: 'richText',
        },
      ],
    },
    table: {
      path: ['requires'],
      control: 'featureComparison',
      selector: '[data-part="table"]',
      placement: 'top-end',
      label: 'Table',
    },
    action: {
      path: ['requires', 'forwards'],
      control: 'ctaAppearance',
      selector: '[data-part="action"]',
      placement: 'top-end',
      label: 'Action',
    },
    otherActions: {
      path: ['requires', 'other_actions'],
      control: 'ctaAppearance',
      selector: '[data-part="other-action"]',
      placement: 'top-end',
      label: 'Action',
      indexAttribute: 'data-action-index',
    },
  } satisfies InlineEditorHints,
});
export default LossFeatureComparison;
